import React, { useEffect, useMemo, useState } from 'react';
import {
    listMaterialCategories,
    MaterialCategoryNode,
} from '../api/materialCategory';
import { listDict, DictEntry } from '../api/dict';
import SearchSelect from './SearchSelect';
import { selLayoutCls, zhOptions } from '../utils/searchOptions';

// 材料主数据表单的公共控件 (新建 modal 与详情页 BasicEditor 共用):
// - CategoryCascade: 类别1/类别2 级联下拉, 选项来自类别管理列表 (后端也会强校验)
// - FlexibilitySelect: 材料柔韧性 (硬性/软性)
// - TraitMultiSelect: 材料特性多选, 选项来自 dict material_trait
// - RollRatioInput: 卷料传送比 (> 0, 最多 5 位小数, 与 CMS 录入同口径)

/** 卷料传送比最多小数位 (列 decimal(11,5), 与 CMS 一致) */
const ROLL_RATIO_DECIMALS = 5;

/**
 * 卷料传送比输入框: 清空 = 0 (编辑时后端按 0 清空, 新建时视为未配置);
 * 超过 5 位小数的输入直接拦下不写入.
 */
export function RollRatioInput({
    value,
    onChange,
    className,
}: {
    value?: number | null;
    onChange: (v: number) => void;
    className?: string;
}) {
    return (
        <input
            type="number"
            min="0"
            step="0.00001"
            className={className}
            value={value || ''}
            placeholder={`请输入, 最多${ROLL_RATIO_DECIMALS}位小数`}
            onChange={(e) => {
                const raw = e.target.value;
                if ((raw.split('.')[1] || '').length > ROLL_RATIO_DECIMALS) {
                    return;
                }
                const v = Number(raw);
                onChange(raw === '' || isNaN(v) ? 0 : v);
            }}
        />
    );
}

export const FLEXIBILITY_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '', label: '(未选)' },
    { value: 'rigid', label: '硬性' },
    { value: 'flexible', label: '软性' },
];

// ---- 模块级缓存 (同 CapTypeBadges 的模式) --------------------------------
// 类别树/特性字典是小静态数据, 但一个页面常有多个组件实例各自调 hook
// (材料库页 = 列表筛选 + 新建弹窗), 叠加 StrictMode 双跑 effect 后同一接口
// 会被请求 4 次. 缓存挂在模块级: 并发去重 (共享 in-flight promise) + 结果复用,
// 管理页写操作后调 invalidateXxx 让下次重新拉取.

let categoryTreeCache: MaterialCategoryNode[] | null = null;
let categoryTreePromise: Promise<MaterialCategoryNode[]> | null = null;

function fetchCategoryTreeCached(): Promise<MaterialCategoryNode[]> {
    if (categoryTreeCache) return Promise.resolve(categoryTreeCache);
    if (!categoryTreePromise) {
        categoryTreePromise = listMaterialCategories()
            .then((r) => {
                categoryTreeCache = r || [];
                return categoryTreeCache;
            })
            .catch((e) => {
                categoryTreePromise = null; // 失败不缓存, 下次重试
                throw e;
            });
    }
    return categoryTreePromise;
}

/** 类别管理页增删改后调用, 让全站类别下拉下次重新拉取. */
export function invalidateCategoryTreeCache() {
    categoryTreeCache = null;
    categoryTreePromise = null;
}

let traitsCache: DictEntry[] | null = null;
let traitsPromise: Promise<DictEntry[]> | null = null;

function fetchTraitsCached(): Promise<DictEntry[]> {
    if (traitsCache) return Promise.resolve(traitsCache);
    if (!traitsPromise) {
        traitsPromise = listDict('material_trait')
            .then((r) => {
                traitsCache = r || [];
                return traitsCache;
            })
            .catch((e) => {
                traitsPromise = null;
                throw e;
            });
    }
    return traitsPromise;
}

/** 类别树 (仅启用项). 全站共享一次请求, 见上方缓存说明. */
export function useCategoryTree(): MaterialCategoryNode[] {
    const [tree, setTree] = useState<MaterialCategoryNode[]>(
        categoryTreeCache || [],
    );
    useEffect(() => {
        let alive = true;
        fetchCategoryTreeCached()
            .then((r) => {
                if (alive) setTree(r);
            })
            .catch(() => {
                if (alive) setTree([]);
            });
        return () => {
            alive = false;
        };
    }, []);
    return tree;
}

/**
 * 分类展示用中文名: material.type1/type2 存的是类别编码 categoryCode, 展示统一转 nameZh.
 * 兼容基线数据里已存中文的取值; 树中查不到 (如类别被删) 时原样返回, 不吞值.
 */
export function useCategoryZh() {
    const tree = useCategoryTree();
    return useMemo(() => {
        const findL1 = (t1: string) =>
            tree.find((t) => t.categoryCode === t1 || t.nameZh === t1);
        const cat1Zh = (t1?: string) => (t1 ? findL1(t1)?.nameZh || t1 : '');
        const cat2Zh = (t1?: string, t2?: string) => {
            if (!t2) return '';
            // 优先在所属一级下找, 找不到再全树兜底 (历史数据 type1/type2 可能不配套)
            const pool =
                (t1 && findL1(t1)?.children) || tree.flatMap((t) => t.children);
            return (
                pool.find((c) => c.categoryCode === t2 || c.nameZh === t2)?.nameZh ||
                t2
            );
        };
        return { cat1Zh, cat2Zh };
    }, [tree]);
}

/** 材料特性字典 (仅启用项). 全站共享一次请求. */
export function useMaterialTraits(): DictEntry[] {
    const [traits, setTraits] = useState<DictEntry[]>(traitsCache || []);
    useEffect(() => {
        let alive = true;
        fetchTraitsCached()
            .then((r) => {
                if (alive) setTraits(r);
            })
            .catch(() => {
                if (alive) setTraits([]);
            });
        return () => {
            alive = false;
        };
    }, []);
    return traits;
}

export function CategoryCascade({
    tree,
    type1,
    type2,
    onChange,
    selectCls = 'sel',
    searchable = false,
    placeholders = ['(未选)', '(未选)'],
}: {
    tree: MaterialCategoryNode[];
    type1: string;
    type2: string;
    onChange: (type1: string, type2: string) => void;
    /** 下拉的宽度/布局类 (如 w-36); 兼容旧调用传入的 .sel */
    selectCls?: string;
    /** 筛选栏用: 选项只显示中文名 (重名补 code); 默认 (编辑表单) 显示 "中文 / English" */
    searchable?: boolean;
    /** searchable 时一级/二级的空值占位文案 */
    placeholders?: [string, string];
}) {
    const current = tree.find((t) => t.categoryCode === type1);
    // 历史数据的类别可能不在管理列表里 (如旧的 Textile), 保留为一个禁用提示项, 强制重选.
    const type1Missing = type1 !== '' && !current;
    const type2Missing =
        type2 !== '' &&
        (!current || !current.children.some((c) => c.categoryCode === type2));
    // 筛选栏只显示中文名 (重名补 code); 编辑表单显示 "中文 / English", 维护人员要核对英文业务键
    const opts1 = searchable
        ? zhOptions(
              tree,
              (t) => t.categoryCode,
              (t) => t.nameZh,
          )
        : tree.map((t) => ({
              value: t.categoryCode,
              label: `${t.nameZh} / ${t.nameEn}`,
          }));
    const children = current?.children || [];
    const opts2 = searchable
        ? zhOptions(
              children,
              (c) => c.categoryCode,
              (c) => c.nameZh,
          )
        : children.map((c) => ({
              value: c.categoryCode,
              label: `${c.nameZh} / ${c.nameEn}`,
          }));
    const cls = searchable ? selectCls : selLayoutCls(selectCls);
    return (
        <>
            <SearchSelect
                className={cls}
                value={type1}
                valueLabel={
                    type1Missing ? `${type1} (已不在管理列表)` : undefined
                }
                onChange={(v) => onChange(v, '')}
                placeholder={placeholders[0]}
                options={opts1}
                clearable
            />
            <SearchSelect
                className={cls}
                value={type2}
                valueLabel={
                    type2Missing ? `${type2} (已不在管理列表)` : undefined
                }
                onChange={(v) => onChange(type1, v)}
                placeholder={placeholders[1]}
                options={opts2}
                disabled={!current}
                clearable
            />
        </>
    );
}

export function TraitMultiSelect({
    traits,
    value,
    onChange,
}: {
    traits: DictEntry[];
    value: string[];
    onChange: (codes: string[]) => void;
}) {
    const toggle = (code: string) => {
        if (value.includes(code)) {
            onChange(value.filter((c) => c !== code));
        } else {
            onChange([...value, code]);
        }
    };
    return (
        <div className="flex flex-wrap gap-1.5">
            {traits.map((t) => {
                const on = value.includes(t.code);
                return (
                    <span
                        key={t.code}
                        className={`tag cursor-pointer select-none ${
                            on
                                ? 'cap-on'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        title={t.code}
                        onClick={() => toggle(t.code)}
                    >
                        {on ? '✓ ' : ''}
                        {t.label}
                    </span>
                );
            })}
            {traits.length === 0 && (
                <span className="text-[11px] text-slate-400">
                    暂无特性选项 (dict material_trait)
                </span>
            )}
        </div>
    );
}
